import type { Biometric, BiometricPrice } from "@/lib/api_client";

/**
 * The client-side mirror of the CRM cost SQL. Three call sites had their own
 * copy of these two rules and drifted apart; keep the mirror in one place so a
 * change to the SQL has one place to land.
 */

export const FURST_PRICE_SOURCE = "FURST_DISCOUNTED";

/**
 * biometricId → price. `source` is free text and imported rows do not all share
 * this casing, so match it case-insensitively — and where two rows qualify take
 * the higher, the way `ORDER BY bp.price DESC NULLS LAST` does in SQL. A plain
 * `map.set` would be last-write-wins on array order.
 */
export function buildFurstPriceMap(
  prices: readonly BiometricPrice[],
): Map<string, number> {
  const map = new Map<string, number>();
  for (const bp of prices) {
    if (bp.source?.toUpperCase() !== FURST_PRICE_SOURCE) continue;
    const p = parseFloat(bp.price);
    if (isNaN(p)) continue;
    map.set(bp.biometricId, Math.max(map.get(bp.biometricId) ?? 0, p));
  }
  return map;
}

/**
 * What a marker is charged under. A blank Fürst code is not a code — it falls
 * back to the marker's own id so unmapped markers stay apart. Mirrors
 * `COALESCE(NULLIF(b.furst_id, ''), b.id::text)`; note `||` would be wrong here,
 * since the code "0" is a code.
 */
export function furstMarkerKey(
  biometricId: string,
  bio: Biometric | undefined,
): string {
  const furstId = bio?.furstId ?? "";
  return furstId !== "" ? furstId : biometricId;
}

/**
 * One charge per Fürst analysis code, at the highest price among the markers
 * behind it — so the estimate cannot land under what Fürst actually bills.
 */
export function sumFurstCost(
  biometricIds: Iterable<string>,
  bioMap: Map<string, Biometric>,
  priceMap: Map<string, number>,
): number {
  const priceByCode = new Map<string, number>();
  for (const id of biometricIds) {
    const price = priceMap.get(id) ?? 0;
    if (price <= 0) continue;
    const code = furstMarkerKey(id, bioMap.get(id));
    priceByCode.set(code, Math.max(priceByCode.get(code) ?? 0, price));
  }
  let sum = 0;
  for (const price of priceByCode.values()) sum += price;
  return sum;
}
